import { computed, ref, type Ref } from 'vue'

/**
 * Status labels for Planet Tasking orders.
 * Maps numeric status codes to human-readable labels.
 */
export const TASKING_STATUS_LABELS: Record<string, string> = {
  '100': 'Pending Approval',
  '0': 'Completed',
  '1': 'Initializing',
  '2': 'Creating tasking order',
  '3': 'Waiting for capture',
  '4': 'Ordering image (legacy)', // Deprecated - kept for backwards compatibility
  '-1': 'Failed: Initialization',
  '-2': 'Failed: Creating tasking',
  '-3': 'Failed: Capture',
  '-4': 'Cancelled',
  '-5': 'Failed: Ordering image (legacy)', // Deprecated - kept for backwards compatibility
  '-100': 'Rejected',
}

/**
 * Status labels for Layer Order (hi-res) statuses.
 * Maps numeric status codes to human-readable labels.
 */
export const ORDER_STATUS_LABELS: Record<string, string> = {
  '100': 'Pending Approval',
  '4': 'Creating Tiles',
  '3': 'Waiting for Result',
  '2': 'Activating',
  '1': 'Initializing',
  '0': 'Done',
  '-1': 'Failed',
  '-2': 'Failed',
  '-3': 'Failed',
  '-4': 'Failed',
  '-100': 'Rejected',
}

/**
 * Get human-readable label for an order status code.
 */
export function getOrderStatusLabel(status: number | string): string {
  return ORDER_STATUS_LABELS[String(status)] || 'Unknown'
}

/**
 * Get capture time from various possible field names in capture data.
 */
export function getCaptureTime(capture: Record<string, any>): string | null {
  return capture.published_time || capture.acquired_time || capture.capture_time || capture.created_time || null
}

/**
 * Get capture footprint geometry from various possible field names.
 */
export function getCaptureFootprint(capture: Record<string, any>): object | null {
  return capture.collected_geometry || capture.footprint || capture.geometry || null
}

/**
 * Get evaluation status from various possible field names.
 */
export function getEvaluationStatus(capture: Record<string, any>): string {
  return capture.evaluation || capture.evaluation_status || capture.review_outcome || 'PENDING'
}

// Module-level shared hover state (shared across all components using this composable)
const hoveredCaptureId = ref<string | null>(null)

/**
 * Composable for shared tasking capture hover state.
 * Used to synchronize hover between capture cards and map polygons.
 */
export function useTaskingHover() {
  const setHoveredCapture = (id: string | null) => {
    hoveredCaptureId.value = id
  }

  const clearHoveredCapture = () => {
    hoveredCaptureId.value = null
  }

  return {
    hoveredCaptureId,
    setHoveredCapture,
    clearHoveredCapture
  }
}

/**
 * Composable for tasking status computed properties.
 *
 * @param status - Reactive ref containing the status value (number or numeric string)
 * @returns Object with computed status booleans and label
 */
export function useTaskingStatus(status: Ref<number | string>) {
  const normalizedStatus = computed(() => parseInt(String(status.value)))

  const isPendingApproval = computed(() => normalizedStatus.value === 100)
  const isRejected = computed(() => normalizedStatus.value === -100)
  const isDone = computed(() => normalizedStatus.value === 0)
  const isCancelled = computed(() => normalizedStatus.value === -4)

  const isFailed = computed(() => {
    const s = normalizedStatus.value
    return s < 0 && s !== -4 && s !== -100
  })

  const isInProgress = computed(() => {
    const s = normalizedStatus.value
    return s > 0 && s !== 100
  })

  const statusLabel = computed(() => TASKING_STATUS_LABELS[String(normalizedStatus.value)] || 'Unknown')

  return {
    isPendingApproval,
    isRejected,
    isDone,
    isCancelled,
    isFailed,
    isInProgress,
    statusLabel
  }
}
